// actualidad.js — los posts editoriales, desde el servidor de la radio.
//
// Contrato: `ACTUALIDAD_WEB_INSTRUCCIONES.md` (servidor, 26.08.26).
// Endpoint: https://listen.astra.fm/actualidad.json · CORS abierto · `max-age=0, must-revalidate` (304 por ETag; medido 05.09.26).
//
// **Sustituye al manager y a WordPress como fuente.** Lo que sirve este JSON
// es el espejo CURADO: la redacción revisa y corrige los posts en el dashboard
// `/studio/actualidad.html`, y lo que sale aquí es esa versión revisada. Leer
// del manager directo —`public-news-api.php`, que es lo que había— se saltaba
// esa revisión.
//
// 🔴 **Y trae la imagen apaisada**, que era la deuda abierta desde el 26.08:
// el manager solo publicaba `imagen` (el archivo completo de WordPress) e
// `imagen_medium` (la miniatura cuadrada de 150), y entre las dos no había
// nada. Medido contra el servidor nuevo: **800×600**, alguna 800×450, de 18 a
// 171 KB. El original de WordPress eran 1448×1086 y 1,5 MB. Con esto sobra
// `wpImage`: aquí no hay derivados que adivinar, la imagen ya viene hecha.
//
// ⚠️ Cuatro cosas del contrato:
//
//   1. **`posts` es un objeto indexado por slug**, no un array. El slug es la
//      URL del post.
//   2. **Hay borradores dentro.** Vienen a propósito —la redacción los revisa
//      en el dashboard— y la web solo debe pintar `status: "published"`. A
//      26.08 son 3 de 93.
//   3. **`imagenDestacada` es ruta relativa y puede ser `null`.** Tres posts no
//      tienen. No construirla a mano desde el slug: la fuente de verdad es el
//      campo.
//   4. **Las imágenes y los iframes DENTRO de `contenidoHtml` siguen en
//      WordPress.** Solo la destacada está espejada. Se renderizan tal cual.
//
// El contrato no publica autor: la firma la resuelve quien pinta.

import { LISTEN } from "./host";

const BASE = `${LISTEN}`;
const URL_ACTUALIDAD = `${BASE}/actualidad.json`;
const TTL = 5 * 60 * 1000;

let cache = null;
let cacheAt = 0;
let enVuelo = null;

/**
 * Olvida lo guardado y obliga a preguntar de nuevo.
 *
 * Hace falta porque la caché de arriba vive en el módulo: sobrevive a cambiar
 * de vista y solo caduca por tiempo. Sin esto, publicar algo y volver a la
 * pestaña no servía de nada — se seguía sirviendo lo aprendido hace cuatro
 * minutos.
 */
export function invalidar() {
  cache = null;
  cacheAt = 0;
}

// Las tres subsecciones. El `tipo` del contrato ya es el slug que usa la web
// en `?categoria=`, así que no hay traducción que hacer: solo la etiqueta.
const ETIQUETAS = {
  historias: "Historias",
  lanzamientos: "Lanzamientos",
  noticias: "Noticias",
};

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/**
 * «2026-08-22 07:34:17» → «22.08.26».
 *
 * La fecha de una celda de retícula, donde comparte renglón con el tipo de
 * pieza y no hay sitio para el mes escrito. Entrega
 * `docs/entregas/2026-09-18-actualidad-en-la-portada.md`.
 *
 * ⚠️ **Lleva el año**, a diferencia del «22 AGO» de Lanzamientos: aquel pinta
 * discos del año en curso y este un archivo que llega hasta 2024.
 */
function enCorto(fecha) {
  const [y, m, d] = (fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  const dosCifras = (n) => String(n).padStart(2, "0");
  return `${dosCifras(d)}.${dosCifras(m)}.${String(y).slice(2)}`;
}

/** «2026-08-22 07:34:17» → «22 de agosto de 2026». */
function enLargo(fecha) {
  const [y, m, d] = (fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} de ${MESES[m - 1]} de ${y}`;
}

// Forma única para toda la web, y la MISMA que devolvía el manager: las vistas
// no deben enterarse de que la fuente ha cambiado. Si mañana cambia otra vez,
// se traduce aquí y solo aquí.
function normaliza(slug, p) {
  const tipo = p.tipo || "";
  return {
    id: p.managerId ?? slug,
    slug,
    titulo: p.titulo || "",

    // Ruta relativa → absoluta. Vacía si el post no tiene imagen.
    imagen: p.imagenDestacada ? BASE + p.imagenDestacada : "",

    // Un array de uno: la web pinta la primera y el contrato da un solo tipo.
    categorias: tipo
      ? [{ id: tipo, nombre: ETIQUETAS[tipo] || tipo, slug: tipo }]
      : [],

    // 🔴 Solo la parte de fecha. El contrato la da con hora —«2026-08-22
    // 07:34:17»— y quien la lee hace `new Date(fecha + "T00:00:00")`, que con
    // la hora pegada da fecha inválida y cae al texto largo sin avisar.
    fecha: (p.fecha || "").slice(0, 10),
    fechaFormateada: enLargo(p.fecha),
    fechaCorta: enCorto(p.fecha),

    extracto: p.excerpt || "",
    contenido: p.contenidoHtml || "",

    // Para <title> y <meta>. El manager no los daba; si faltan, la vista tira
    // de `titulo` y `extracto` como hacía antes.
    metaTitle: p.metaTitle || "",
    metaDescription: p.metaDescription || "",

    // Orden interno, para no re-parsear la fecha en cada comparación.
    _orden: p.fecha || "",
  };
}

/**
 * Todos los publicados, normalizados y del más nuevo al más viejo.
 * Ante un fallo de red devuelve el último caché que hubiera; si nunca hubo, un
 * array vacío. Nunca lanza: sin actualidad la web sigue sonando.
 */
export async function getActualidad() {
  const ahora = Date.now();
  if (cache && ahora - cacheAt < TTL) return cache;

  // Portada y archivo montando a la vez piden lo mismo; se comparte la
  // petición. Son 424 KB: pedirlos dos veces se nota.
  if (enVuelo) return enVuelo;

  enVuelo = (async () => {
    try {
      const r = await fetch(URL_ACTUALIDAD);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();

      const lista = Object.entries(data.posts || {})
        .filter(([, p]) => p?.status === "published")
        .map(([slug, p]) => normaliza(slug, p))
        .sort((a, b) => b._orden.localeCompare(a._orden));

      if (lista.length) {
        cache = lista;
        cacheAt = ahora;
      }
      return cache || [];
    } catch (error) {
      // Red caída: mejor la actualidad de hace un rato que ninguna.
      return cache || [];
    } finally {
      enVuelo = null;
    }
  })();

  return enVuelo;
}

/**
 * Un trozo del archivo.
 *
 * @param {number} limit  Cuántos como mucho. `0` o negativo: todos.
 * @param {string} tipo   `historias` | `lanzamientos` | `noticias`, o nada.
 * @param {number} days   Solo los de los últimos N días, o nada.
 */
export async function listActualidad(limit = 0, tipo = null, days = null) {
  let lista = await getActualidad();

  if (tipo) lista = lista.filter((p) => p.categorias[0]?.slug === tipo);

  if (days > 0) {
    // Contra la fecha, no contra la hora: un post de esta mañana y otro de
    // hace seis días y medio entran los dos en «la última semana».
    const desde = new Date();
    desde.setDate(desde.getDate() - days);
    const corte = desde.toISOString().slice(0, 10);
    lista = lista.filter((p) => p.fecha >= corte);
  }

  return limit > 0 ? lista.slice(0, limit) : lista;
}

/** Un post por su slug, o `null` si no existe o no está publicado. */
export async function getPost(slug) {
  if (!slug) return null;
  const lista = await getActualidad();
  return lista.find((p) => p.slug === slug) || null;
}
